<template>
  <div id="vessel-live-map-container" class="vs-con-loading__container">
    <div v-show="hasLiveMapError" class="map-overlay-container-no-info">
      <div class="map-overlay-no-info">
        <p class="text-2xl font-semibold">
          Live情報が取得できませんでした
        </p>
      </div>
    </div>
    <div v-show="!hasLiveMapError" :id="'vessel-live-map' + uniqueKey" class="vessel-live-map-full"></div>
    <div class="mapboxgl-ctrl-bottom-right">
      <div id="vessel-style-change" class="mapboxgl-ctrl">
        <map-style-changer :map="map"></map-style-changer>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import {createNamespacedHelpers} from "vuex";
import {db} from "@/firebase/firebaseConfig";
import Mapbox from "mapbox-gl";
import {LiveVesselMapLayer, LiveVesselMapSource} from "@/config/Map.js";
import MapStyleChanger from "./MapStyleChanger";
import LocalDbService from "@/services/localDbService";
import AuthService from "@/services/authService.js";
import moment from "moment-timezone";

// utils
import {WakeLineUtil, DateUtil} from "@/utils";

// Store
const {
  mapActions: mapActionsOfVesselView,
  mapState: mapStateOfLiveVesselView,
} = createNamespacedHelpers("enterpriseVesselView");

// Mixins
import {LocalizeMixin, LayerMixin} from "@/mixins";

export default {
  name: "VesselLiveMap",
  mixins: [LocalizeMixin, LayerMixin],
  components: {
    MapStyleChanger
  },
  data() {
    return {
      initialized: false,
      hasLiveMapError: false,
      map: null,
      accessToken: process.env.VUE_APP_MAPBOX_TOKEN,
      center: [139.86448000000001, 35.54817666666667],
      zoom: 13,
      firestoreUnsubscribes: [],
      aisInfo: null,
      vesselLivePosition: null,
      isStyleChange: false,
      wakes: [],
      unSubscribeStore: null,
      expiredPopup: null,
      observerVesselPosition: null,
      timerId: null,
      popupForWake: new Mapbox.Popup({
        closeButton: false,
        closeOnClick: false
      }),
      mapStyleLocal: null
    };
  },
  props: {
    vesselId: {type: String, required: true},
    uniqueKey: {type: String, default: ""}
  },
  computed: {
    ...mapStateOfLiveVesselView({
      mapStyle: state => state.mapStyle,
      voyageHistoryDateTime: state => state.voyageHistoryDateTime,
      vessel: state => state.vessel
    })
  },
  async created() {
    this.map = null;
  },
  async mounted() {
    this.mapStyleLocal = await LocalDbService.getMapStyle();
    Mapbox.prewarm();
    Mapbox.workerCount = this.getWorkerCount();
    Mapbox.maxParallelImageRequests = 10;
    Mapbox.accessToken = this.accessToken;
    this.map = new Mapbox.Map({
      container: "vessel-live-map" + this.uniqueKey,
      style: this.mapStyleLocal,
      center: this.center,
      zoom: this.zoom
    });

    let nav = new Mapbox.NavigationControl();
    this.map.addControl(nav, "bottom-right");
    this.map.on("style.load", this.mapStyleLoad);
    this.unSubscribeStore = this.$store.subscribe((mutation, _) => {
      if (mutation.type === "TOGGLE_IS_VERTICAL_NAV_MENU_ACTIVE") {
        if (this.map && !this.doMapResize) {
          this.doMapResize = true;
          setTimeout(() => {
            this.map.resize();
            this.doMapResize = false;
          }, 1000);
        }
      }
    });
  },
  watch: {
    mapStyle(val) {
      this.isStyleChange = true;
      this.map.setStyle(val);
      this.mapStyleLocal = val
    },
    async vesselLivePosition(newPosition) {
      let vesselName =
        this.vessel.name_ja !== null ? this.vessel.name_ja : this.vessel.name;
      if (this.wakes.length === 0) {
        let payload = {
          vesselId: this.vesselId,
          vesselName: vesselName
        };
        this.wakes = await this.fetchWakeLine(payload);
      }
      let payload = {
        vesselId: this.vesselId,
        position: newPosition,
        ais: this.aisInfo
      };
      let res = await this.crateLiveVesselsFeatureCollections(payload);
      this.setSources(res);

      let wakeDate = {
        lat: newPosition.l.latitude,
        lon: newPosition.l.longitude,
        v: newPosition.v,
        t: newPosition.t,
        opacity: 0.9,
        color: WakeLineUtil.getVelocityColor(newPosition.v),
        localDate: moment
          .utc(newPosition.t)
          .local()
          .format("YYYY/MM/DD HH:mm"),
        vesselName: vesselName
      };

      this.wakes.push(wakeDate);
      await this.setWakeLineSources();

      this.$vs.loading.close("#vessel-live-map-container > .con-vs-loading");

      if (!this.initialized) {
        let coordinates = {
          center: [newPosition.l._long, newPosition.l._lat]
        };
        this.map.jumpTo(coordinates);
        this.map.zoomTo(9);
        this.initialized = true;
      }
    }
  },
  methods: {
    ...mapActionsOfVesselView([
      "setVesselLivePosition",
      "setVesselLiveAisInfo",
      "crateLiveVesselsFeatureCollections",
      "fetchWakeLine",
      "crateWakeLineFeatureCollection",
      "getLastVesselPosition",
      "setVesselPositionExpired10min",
      "setMapStyle"
    ]),

    getWorkerCount() {
      return navigator.hardwareConcurrency / 2 + 1;
    },

    async mapStyleLoad(_a, _b) {
      if (this.isDefaultMapStyle(this.mapStyleLocal)) {
        let lang = this.getBrowserLanguage()
        let local = "name_" + lang;
        if (this.map) {
          let textLayerIds = this.getTextLayerIds();
          textLayerIds.forEach(id => {
            this.map.setLayoutProperty(id, "text-field", ["get", local]);
          });
        }
      }
      this.unsubscribeFireStore();
      this.cratePulsing(this.map);
      this.addSources();
      this.addLayers();
      this.setMouseEvents();
      await this.setLiveData();
      if (this.isStyleChange) {
        this.$vs.loading({
          container: "#vessel-live-map-container",
          text: "地図スタイルを変更中..."
        });
      } else {
        this.$vs.loading({
          container: "#vessel-live-map-container",
          text: "Liveデータを取得中..."
        });
      }
    },

    addLayers() {
      this.map.addImage("pulsing", this.pulsing, {pixelRatio: 2});
      this.map.addLayer({
        id: LiveVesselMapLayer.LiveVesselPulsingLayer.id,
        type: "symbol",
        source: LiveVesselMapSource.LiveVesselPulsingSource.id,
        maxzoom: 15,
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-image": "pulsing"
        }
      });

      this.map.addLayer({
        id: LiveVesselMapLayer.LiveVesselWakeLineLayer.id,
        type: "line",
        source: LiveVesselMapSource.LiveVesselWakeLineSource.id,
        paint: {
          "line-color": ["get", "color"],
          "line-width": 2.0,
          "line-opacity": ["get", "opacity"],
          "line-dasharray": [1, 1]
        }
      });

      this.map.addLayer({
        id: LiveVesselMapLayer.LiveVesselWakeLineLayerForClickEvent.id,
        type: "line",
        source: LiveVesselMapSource.LiveVesselWakeLineSourceForClickEvent.id,
        paint: {
          "line-width": 20.0,
          "line-opacity": 0.0
        }
      });

      this.map.addLayer({
        id: LiveVesselMapLayer.LiveVesselVectorLayer.id,
        type: "line",
        source: LiveVesselMapSource.LiveVesselVectorSource.id,
        minzoom: 8,
        maxzoom: 24,
        layout: {
          "line-join": "round",
          "line-cap": "round"
        },
        paint: {
          "line-color": "#DA514B",
          "line-width": 2.0,
          "line-opacity": {
            type: "identity",
            property: "o"
          }
        }
      });

      this.map.addLayer({
        id: LiveVesselMapLayer.LiveVesselPolygonLayer.id,
        type: "fill",
        source: LiveVesselMapSource.LiveVesselPolygonSource.id,
        minzoom: 14,
        maxzoom: 24,
        paint: {
          "fill-color": {
            type: "identity",
            property: "co"
          }
        }
      });

      this.map.addLayer({
        id: LiveVesselMapLayer.LiveVesselSymbolLayer.id,
        type: "symbol",
        source: LiveVesselMapSource.LiveVesselSymbolSource.id,
        minzoom: 1,
        maxzoom: 14,
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-pitch-alignment": "map",
          "icon-rotation-alignment": "map",
          "icon-image": "{i}",
          "icon-rotate": {
            type: "identity",
            property: "r"
          }
        },
        paint: {
          "icon-opacity": {
            type: "identity",
            property: "o"
          }
        }
      });

      this.map.addLayer({
        id: LiveVesselMapLayer.LiveVesselSymbolWithoutSizeLayer.id,
        type: "symbol",
        source: LiveVesselMapSource.LiveVesselSymbolSource.id,
        minzoom: 14,
        filter: ["all", ["==", ["number", ["get", "is_size"]], 0]],
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-pitch-alignment": "map",
          "icon-rotation-alignment": "map",
          "icon-image": "{i}",
          "icon-rotate": {
            type: "identity",
            property: "r"
          }
        },
        paint: {
          "icon-opacity": {
            type: "identity",
            property: "o"
          }
        }
      });

      this.map.addLayer({
        id: LiveVesselMapLayer.LiveVesselTextLayer.id,
        type: "symbol",
        source: LiveVesselMapSource.LiveVesselSymbolSource.id,
        layout: {
          "text-field": "{n}",
          "text-anchor": "top",
          "text-size": 14,
          "text-line-height": 1.2,
          "text-offset": [0, 1.1],
          "text-allow-overlap": true,
          "text-ignore-placement": true
        },
        paint: {
          "text-color": "#54B1FC",
          "text-halo-color": "#000000",
          "text-halo-width": 1
        }
      });
    },

    addSources() {
      this.map.addSource(LiveVesselMapSource.LiveVesselPulsingSource.id, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: []
        }
      });

      this.map.addSource(LiveVesselMapSource.LiveVesselWakeLineSource.id, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: []
        }
      });

      this.map.addSource(
        LiveVesselMapSource.LiveVesselWakeLineSourceForClickEvent.id,
        {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: []
          }
        }
      );

      this.map.addSource(LiveVesselMapSource.LiveVesselVectorSource.id, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: []
        }
      });

      this.map.addSource(LiveVesselMapSource.LiveVesselPolygonSource.id, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: []
        }
      });

      this.map.addSource(LiveVesselMapSource.LiveVesselWithoutSizeSource.id, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: []
        }
      });

      this.map.addSource(LiveVesselMapSource.LiveVesselSymbolSource.id, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: []
        }
      });
    },

    setSources(featureCollections) {
      const pulsingSource = this.map.getSource(
        LiveVesselMapSource.LiveVesselPulsingSource.id
      );
      if (pulsingSource != null) {
        pulsingSource.setData(featureCollections.symbolFeatureCollection);
      }

      const pointSource = this.map.getSource(
        LiveVesselMapSource.LiveVesselSymbolSource.id
      );
      if (pointSource != null) {
        pointSource.setData(featureCollections.symbolFeatureCollection);
      }

      const polygonSource = this.map.getSource(
        LiveVesselMapSource.LiveVesselPolygonSource.id
      );
      if (polygonSource != null) {
        polygonSource.setData(featureCollections.polygonFeatureCollection);
      }

      const vectorSource = this.map.getSource(
        LiveVesselMapSource.LiveVesselVectorSource.id
      );
      if (vectorSource != null) {
        vectorSource.setData(featureCollections.polygonFeatureCollection);
      }
    },

    setMouseEvents() {
      let self = this;
      this.map.on(
        "mousemove",
        LiveVesselMapLayer.LiveVesselWakeLineLayerForClickEvent.id,
        e => {
          self.setCursor(true, self);
          self.showWakeLineInfoPopup(e, self);
        }
      );
      this.map.on(
        "mouseleave",
        LiveVesselMapLayer.LiveVesselWakeLineLayerForClickEvent.id,
        () => {
          self.setCursor(false, self);
          self.removePopup(self);
        }
      );
    },

    setCursor(isPointer, self) {
      self.map.getCanvas().style.cursor = isPointer ? "pointer" : "";
    },

    showWakeLineInfoPopup(e, self) {
      let properties = e.features[0].properties;
      let coordinates = [e.lngLat.lng, e.lngLat.lat];
      let speedLabel = this.$t("Views.Vessel.Velocity");
      let speed = properties.speed;
      let dataTimeLabel = this.$t("Views.Commons.DataTime");
      let dataTime = properties.localDate;
      let vesselName = properties.vesselName;
      let wakeLineLabel = this.$t("Views.Vessel.WakeLine");
      let description = `<strong>${vesselName}</strong><span>の${wakeLineLabel}</span><br><span class="mr-2">${speedLabel}: <strong>${speed} kn</strong></span><span>${dataTimeLabel}: <strong>${dataTime}</strong></span>`;
      self.popupForWake
        .setLngLat(coordinates)
        .setHTML(description)
        .addTo(self.map);
    },

    removePopup(self) {
      self.popupForWake.remove();
    },

    async setWakeLineSources() {
      if (this.wakes.length >= 2) {
        let featureCollection = await this.crateWakeLineFeatureCollection(
          this.wakes
        );
        const source = this.map.getSource(
          LiveVesselMapSource.LiveVesselWakeLineSource.id
        );
        if (source != null) {
          source.setData(featureCollection);
        }
        const sourceForClickEvent = this.map.getSource(
          LiveVesselMapSource.LiveVesselWakeLineSourceForClickEvent.id
        );
        if (sourceForClickEvent != null) {
          sourceForClickEvent.setData(featureCollection);
        }
      }
    },

    async setLiveData() {
      await this.subscribeAisInfo();
    },

    async subscribeVesselPosition() {
      const collectionName = await AuthService.getVesselPositionCollectionName();
      let unsubscribe = await db
        .collection(collectionName)
        .doc(this.vesselId)
        .onSnapshot(
          doc => {
            if (doc.data() !== undefined) {
              let target = moment(doc.data().t);
              if (moment().diff(target, "minutes") > 10) {
                this.crateExpiredPopup();
                this.vesselLivePosition = doc.data();
                this.setVesselLivePosition(doc.data());
                this.setVesselPositionExpired10min(true);
              } else {
                if (this.expiredPopup) {
                  this.expiredPopup.remove();
                  this.expiredPopup = null;
                  this.wakes = [];
                }
                this.setVesselPositionExpired10min(false);
                this.vesselLivePosition = doc.data();
                this.setVesselLivePosition(doc.data());
                this.setObserverExpired(doc.data());
              }
              this.hasLiveMapError = false;
            } else {
              this.$vs.loading.close(
                "#vessel-live-map-container > .con-vs-loading"
              );
              this.hasLiveMapError = true;
            }
          },
          function (error) {
            Vue.$log.error(error);
          }
        );

      this.firestoreUnsubscribes.push(unsubscribe);
    },

    async subscribeAisInfo() {
      let unsubscribe = await db
        .collection("vessel_info")
        .doc(this.vesselId)
        .onSnapshot(
          doc => {
            if (doc.data() !== undefined) {
              this.aisInfo = doc.data();
              this.setVesselLiveAisInfo(doc.data());
              if (!this.initialized || this.isStyleChange) {
                this.subscribeVesselPosition();
                this.isStyleChange = false;
              }
            }
          },
          function (error) {
            Vue.$log.error(error);
          }
        );
      this.firestoreUnsubscribes.push(unsubscribe);
    },

    async crateExpiredPopup() {
      if (this.expiredPopup !== null) {
        this.expiredPopup.remove();
        this.expiredPopup = null;
      }
      if (this.timerId) {
        clearTimeout(this.timerId);
        this.timerId = null;
      }
      let data = await this.getLastVesselPosition(this.vesselId);
      if (data) {
        let dateFormat = moment(data.last_position_at).format(
          "YYYY/MM/DD HH:mm"
        );
        let elapsed = DateUtil.elapsedTime(dateFormat);
        let vesselName =
          this.vessel.name_ja !== null ? this.vessel.name_ja : this.vessel.name;
        this.expiredPopup = new Mapbox.Popup({
          closeOnClick: false,
          closeButton: false,
          offset: 15
        })
          .setLngLat([data.longitude, data.latitude])
          .setText(
            `${vesselName}はこの位置で、${elapsed}(${dateFormat})に記録されました。`
          )
          .addTo(this.map);

        this.expiredPopup.addClassName("expired-vessel-live-position-popup");
      } else {
        this.$vs.loading.close("#vessel-live-map-container > .con-vs-loading");
        this.hasLiveMapError = true;
      }
    },

    unsubscribeFireStore() {
      if (this.firestoreUnsubscribes.length !== 0) {
        this.firestoreUnsubscribes.forEach(unsubscribe => unsubscribe());
        this.firestoreUnsubscribes = [];
      }
    },

    cratePulsing(map) {
      let size = 100;
      this.pulsing = {
        width: size,
        height: size,
        data: new Uint8Array(size * size * 4),
        onAdd: function () {
          let canvas = document.createElement("canvas");
          canvas.width = this.width;
          canvas.height = this.height;
          this.context = canvas.getContext("2d");
        },

        render: function () {
          let duration = 2000;
          let t = (performance.now() % duration) / duration;

          let radius = (size / 2) * 0.3;
          let outerRadius = (size / 2) * 0.7 * t + radius;
          let context = this.context;

          context.clearRect(0, 0, this.width, this.height);
          context.beginPath();
          context.arc(
            this.width / 2,
            this.height / 2,
            outerRadius,
            0,
            Math.PI * 2
          );
          context.fillStyle = "rgba(255, 255, 255," + (1 - t) + ")";
          context.fill();

          this.data = context.getImageData(0, 0, this.width, this.height).data;

          map.triggerRepaint();

          return true;
        }
      };
    },

    // 10分取れなかったら、ポップアップをだす。
    setObserverExpired(data) {
      let target = moment(data.t);
      let timeout = 600000 - moment().diff(target, "millisecond");
      if (this.timerId) {
        clearTimeout(this.timerId);
        this.timerId = null;
      }
      this.timerId = setTimeout(() => {
        this.crateExpiredPopup();
        this.setVesselPositionExpired10min(true);
      }, timeout);
    }
  },

  beforeDestroy() {
    this.wakes = [];
    this.unsubscribeFireStore();
    if (this.unSubscribeStore) {
      this.unSubscribeStore();
    }
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }
};
</script>

<style lang="scss">
#vessel-live-map-container {
  width: 100%;
  height: 100%;

  .vessel-live-map-full {
    width: 100%;
    height: 100%;
    position: relative;
  }

  .mapboxgl-ctrl-bottom-right .mapboxgl-ctrl {
    margin: 0 10px 40px 0 !important;
  }

  #vessel-style-change {
    margin-bottom: 10px !important;
  }
}

.expired-vessel-live-position-popup {
  max-width: 200px;
}

.expired-vessel-live-position-popup > .mapboxgl-popup-tip {
  border-top-color: #333333 !important;
  border-bottom-color: #333333 !important;
}

.expired-vessel-live-position-popup > .mapboxgl-popup-content {
  background-color: #333333 !important;
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 21px;
  color: #f2f2f2;
}
</style>
